import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import moment from "moment";
import { getClientCommentsApi, getClientWeekApi } from "src/DAL/coachWorkspace/coachWorkspace";
import { CW, Panel, Pill, Segments, TextLink } from "./cwUi";

/**
 * P3.4 — "Programme" column on the client review page (design PDF page 2):
 * All tasks / Missed workouts / Completed, "With client comments" filter,
 * View comment, and a full comments dialog.
 * An exercise not ticked = incomplete, even if the workout was saved.
 */

const STATE_LABEL = { completed: "Completed", incomplete: "Incomplete", missed: "Missed", upcoming: "Upcoming" };

export function CommentsDialog({ open, onClose, memberId, focus }) {
  const [type, setType] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !memberId) return;
    let live = true;
    setLoading(true);
    getClientCommentsApi(memberId, type).then((result) => {
      if (!live) return;
      setRows(result?.code === 200 ? result.comments || [] : []);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [open, memberId, type]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
        Client comments
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {focus ? (
          <Box sx={{ p: 1.5, mb: 2, borderRadius: "10px", bgcolor: CW.tealSoft }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: CW.primary }}>
              {focus.typeLabel} · {focus.title} · {moment(focus.date).format("ddd D MMM")}
            </Typography>
            <Typography sx={{ fontSize: 14, color: CW.ink, whiteSpace: "pre-wrap", mt: 0.5 }}>{focus.comment}</Typography>
          </Box>
        ) : null}
        <Segments
          value={type}
          onChange={setType}
          sx={{ mb: 1.5 }}
          options={[
            { value: "all", label: "All" },
            { value: "workout", label: "Workouts" },
            { value: "food", label: "Nutrition" },
            { value: "mindset", label: "Mindset" },
          ]}
        />
        {loading ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CircularProgress size={26} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ color: CW.muted, fontSize: 13.5, py: 2 }}>No comments yet.</Typography>
        ) : (
          rows.map((row) => (
            <Box key={row.id} sx={{ py: 1.25, borderTop: `1px solid ${CW.line}` }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Pill tone="teal">{row.typeLabel}</Pill>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: CW.ink }}>{row.title}</Typography>
                <Typography sx={{ fontSize: 12, color: CW.muted }}>{moment(row.date).format("ddd D MMM YYYY")}</Typography>
              </Box>
              <Typography sx={{ fontSize: 13.5, color: CW.ink, mt: 0.5, whiteSpace: "pre-wrap" }}>{row.comment}</Typography>
              {row.coachComment ? (
                <Typography sx={{ fontSize: 12.5, color: CW.muted, mt: 0.5 }}>Coach: {row.coachComment}</Typography>
              ) : null}
            </Box>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ProgrammeWeekPanel({ memberId, onWeekLoaded, onOpenCalendar, clientFirstName, sx }) {
  const [date, setDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [withComments, setWithComments] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [focus, setFocus] = useState(null);
  const loadedRef = useRef(onWeekLoaded);
  loadedRef.current = onWeekLoaded;

  const load = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    const result = await getClientWeekApi(memberId, date);
    if (result?.code === 200) {
      setData(result);
      if (loadedRef.current) loadedRef.current(result);
    } else setData({ error: result?.message || "Could not load the programme week", tasks: [] });
    setLoading(false);
  }, [memberId, date]);

  useEffect(() => {
    load();
  }, [load]);

  const tasks = useMemo(() => {
    let list = data?.tasks || [];
    if (filter === "missed") list = list.filter((t) => t.state === "missed" || t.state === "incomplete");
    if (filter === "completed") list = list.filter((t) => t.state === "completed");
    if (withComments) list = list.filter((t) => t.comment);
    return list;
  }, [data, filter, withComments]);

  if (!memberId) {
    return (
      <Panel title="Programme" info="review.programme" sx={sx}>
        <Typography sx={{ fontSize: 13, color: CW.muted }}>
          This person is not linked to a member account, so there is no programme to show.
        </Typography>
      </Panel>
    );
  }

  const stateColor = { completed: CW.green, incomplete: CW.amber, missed: CW.amber, upcoming: CW.muted };
  return (
    <Panel
      title="Programme"
      info="review.programme"
      sx={sx}
      action={
        onOpenCalendar ? (
          <TextLink onClick={onOpenCalendar} sx={{ fontSize: 13.5 }}>
            Open calendar ↗
          </TextLink>
        ) : null
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: -0.5, mb: 1.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, ml: -0.75 }}>
          <IconButton size="small" onClick={() => data?.week && setDate(data.week.prev)} aria-label="Previous week">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontSize: 14, color: CW.muted }}>{data?.week?.label || ""}</Typography>
          <IconButton size="small" onClick={() => data?.week && setDate(data.week.next)} aria-label="Next week">
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
        <TextLink onClick={() => data?.week && setDate(data.week.next)} sx={{ fontSize: 13.5 }}>
          Week ahead →
        </TextLink>
      </Box>
      <Segments
        value={filter}
        onChange={setFilter}
        options={[
          { value: "all", label: "All tasks" },
          { value: "missed", label: "Missed workouts" },
          { value: "completed", label: "Completed" },
        ]}
      />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1, mb: 1 }}>
        <FormControlLabel
          sx={{ m: 0 }}
          control={<Checkbox size="small" checked={withComments} onChange={(e) => setWithComments(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 13.5, color: CW.ink }}>With client comments</Typography>}
        />
        <Typography sx={{ fontSize: 12.5, color: CW.muted }}>
          {tasks.length} task{tasks.length === 1 ? "" : "s"} shown
        </Typography>
      </Box>
      {loading && !data ? (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CircularProgress size={26} />
        </Box>
      ) : data?.error ? (
        <Typography sx={{ color: CW.red, fontSize: 13 }}>{data.error}</Typography>
      ) : tasks.length === 0 ? (
        <Typography sx={{ color: CW.muted, fontSize: 13.5, py: 2 }}>No tasks for this filter.</Typography>
      ) : (
        <Box sx={{ display: "grid", gap: 1.25 }}>
          {tasks.map((task) => (
            <Box key={task.id} sx={{ bgcolor: CW.soft, borderRadius: "10px", p: 1.75 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
                <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: CW.ink, minWidth: 0 }}>
                  {task.dayLabel} · {task.title}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: stateColor[task.state] || CW.muted, whiteSpace: "nowrap" }}>
                  {task.state === "completed" ? "✓ " : ""}
                  {STATE_LABEL[task.state]}
                </Typography>
              </Box>
              {task.type === "workout" && task.total > 1 && task.state !== "completed" ? (
                <Typography sx={{ fontSize: 12, color: CW.muted, mt: 0.5 }}>
                  {task.done}/{task.total} exercises ticked
                </Typography>
              ) : null}
              {task.comment ? (
                <>
                  <Typography sx={{ fontSize: 11.5, color: CW.muted, mt: 0.75 }}>
                    {clientFirstName || "Client"} · {moment(task.date).format("D MMM")}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 1, mt: 0.25 }}>
                    <Typography sx={{ fontSize: 13.5, color: CW.ink, minWidth: 0 }} noWrap>
                      “{task.comment}”
                    </Typography>
                    <TextLink
                      sx={{ fontSize: 12.5, whiteSpace: "nowrap" }}
                      onClick={() => {
                        setFocus(task);
                        setCommentsOpen(true);
                      }}
                    >
                      View comment
                    </TextLink>
                  </Box>
                </>
              ) : null}
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mt: 2 }}>
        <TextLink
          sx={{ fontSize: 13.5 }}
          onClick={() => {
            setFocus(null);
            setCommentsOpen(true);
          }}
        >
          All client comments →
        </TextLink>
        {onOpenCalendar ? (
          <TextLink sx={{ fontSize: 13.5 }} onClick={onOpenCalendar}>
            Edit selected week
          </TextLink>
        ) : null}
      </Box>
      <CommentsDialog open={commentsOpen} onClose={() => setCommentsOpen(false)} memberId={memberId} focus={focus} />
    </Panel>
  );
}
